import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, KeyRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { formatApiError } from "@/lib/inmindApi";
import Logo from "@/components/Logo";

export default function RegisterPage() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [adminCode, setAdminCode] = useState("");
  const [showAdminField, setShowAdminField] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await register({
        email, password, name,
        adminCode: adminCode || undefined,
      });
      toast.success(u.role === "admin" ? "Admin account created" : "Welcome to InThink");
      navigate(u.role === "admin" ? "/admin" : "/", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ backgroundColor: "var(--inmind-bg)" }}>
      <div className="max-w-sm w-full" data-testid="register-page">
        <div className="flex items-center gap-3 mb-8">
          <Logo size={44} />
          <div>
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--inmind-muted)]">
              Get started
            </div>
            <h1 className="font-serif text-3xl text-[var(--inmind-text)] leading-none mt-1">InThink</h1>
          </div>
        </div>

        <Card className="rounded-2xl border border-black/5 bg-white shadow-[0_10px_40px_rgba(0,0,0,0.04)]">
          <CardContent className="p-7">
            <h2 className="font-serif text-2xl text-[var(--inmind-text)] mb-1">Create your account</h2>
            <p className="text-sm text-[var(--inmind-text-secondary)] mb-6">A quiet space to think, write, learn.</p>
            <form onSubmit={submit} className="space-y-3">
              <Input
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                data-testid="register-name"
                className="h-11 rounded-xl"
              />
              <Input
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                data-testid="register-email"
                className="h-11 rounded-xl"
              />
              <Input
                type="password"
                placeholder="Password (min 6 chars)"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={6}
                data-testid="register-password"
                className="h-11 rounded-xl"
              />
              {!showAdminField ? (
                <button
                  type="button"
                  onClick={() => setShowAdminField(true)}
                  data-testid="show-admin-code"
                  className="inline-flex items-center gap-1.5 text-xs text-[var(--inmind-text-secondary)] hover:text-[var(--inmind-text)] transition-colors"
                >
                  <KeyRound className="w-3.5 h-3.5" strokeWidth={1.7} />
                  Have an admin code?
                </button>
              ) : (
                <Input
                  placeholder="Admin signup code"
                  value={adminCode}
                  onChange={(e) => setAdminCode(e.target.value)}
                  data-testid="register-admin-code"
                  className="h-11 rounded-xl font-mono text-sm"
                />
              )}
              <Button
                type="submit"
                disabled={loading}
                data-testid="register-submit"
                className="w-full h-11 rounded-xl bg-[var(--inmind-primary)] text-white hover:bg-[var(--inmind-primary-hover)] transition-colors"
              >
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
              </Button>
            </form>
            <div className="text-sm text-[var(--inmind-text-secondary)] mt-5 text-center">
              Already have an account?{" "}
              <Link to="/login" className="text-[var(--inmind-primary)] font-medium hover:underline" data-testid="link-to-login">
                Sign in
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
